import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Eye,
  FolderPlus,
  History,
  LayoutGrid,
  List,
  Menu,
  Pencil,
  Search,
  Share2,
  Star,
  Trash2,
  Upload,
} from 'lucide-react';
import type { DocumentCategory } from '@/services/api/documentService';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';
import type { DocScopeFilter, FileTypeFilter } from '../documentUi';

interface DocumentsToolbarProps {
  isMobile: boolean;
  canManageCategories: boolean;
  selectedFolder: DocumentCategory | null;
  canUpload: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  typeFilter: FileTypeFilter;
  onTypeFilterChange: (value: FileTypeFilter) => void;
  scopeFilter: DocScopeFilter;
  onScopeFilterChange: (value: DocScopeFilter) => void;
  viewMode: 'list' | 'grid';
  onViewModeChange: (mode: 'list' | 'grid') => void;
  restoringFolder: boolean;
  onOpenFolders: () => void;
  onOpenActivity: () => void;
  onShareFolder: () => void;
  onRenameFolder: () => void;
  onHideFolder: () => void;
  onDeleteFolder: () => void;
  canDeleteFolder: boolean;
  onUnhideFolder: () => void;
  onNewFolder: () => void;
  onUpload: () => void;
}

const DocumentsToolbar: React.FC<DocumentsToolbarProps> = ({
  isMobile,
  canManageCategories,
  selectedFolder,
  canUpload,
  searchTerm,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  scopeFilter,
  onScopeFilterChange,
  viewMode,
  onViewModeChange,
  restoringFolder,
  onOpenFolders,
  onOpenActivity,
  onShareFolder,
  onRenameFolder,
  onHideFolder,
  onDeleteFolder,
  canDeleteFolder,
  onUnhideFolder,
  onNewFolder,
  onUpload,
}) => (
  <Card>
    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        alignItems={{ lg: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flex: 1 }} alignItems={{ sm: 'center' }}>
          {isMobile && (
            <Button variant="outlined" startIcon={<Menu size={16} />} onClick={onOpenFolders}>
              Folders
            </Button>
          )}
          <TextField
            size="small"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ width: { xs: '100%', sm: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
          <AppSelect
            size="small"
            label="Type"
            fullWidth={false}
            value={typeFilter}
            onChange={(e) => onTypeFilterChange(e.target.value as FileTypeFilter)}
            formControlProps={{ sx: { width: 148, minWidth: 148, flexShrink: 0 } }}
          >
            <AppMenuItem value="all">All types</AppMenuItem>
            <AppMenuItem value="pdf">PDF</AppMenuItem>
            <AppMenuItem value="word">Word</AppMenuItem>
            <AppMenuItem value="excel">Excel</AppMenuItem>
            <AppMenuItem value="image">Image</AppMenuItem>
            <AppMenuItem value="google_doc">Google Doc</AppMenuItem>
            <AppMenuItem value="google_sheet">Google Sheet</AppMenuItem>
            <AppMenuItem value="drive_link">Drive link</AppMenuItem>
          </AppSelect>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {(
              [
                ['all', 'All'],
                ['recent', 'Recent'],
                ['favorites', 'Favorites'],
              ] as const
            ).map(([value, label]) => (
              <Chip
                key={value}
                size="small"
                label={label}
                icon={value === 'favorites' ? <Star size={12} /> : undefined}
                color={scopeFilter === value ? 'primary' : 'default'}
                variant={scopeFilter === value ? 'filled' : 'outlined'}
                onClick={() => onScopeFilterChange(value)}
              />
            ))}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {!isMobile && (
            <Box sx={{ display: 'flex', bgcolor: 'grey.100', borderRadius: 1, p: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => onViewModeChange('grid')}
                sx={{
                  bgcolor: viewMode === 'grid' ? 'background.paper' : 'transparent',
                  color: viewMode === 'grid' ? 'primary.main' : 'text.secondary',
                  boxShadow: viewMode === 'grid' ? 1 : 0,
                  borderRadius: 1,
                }}
              >
                <LayoutGrid size={16} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onViewModeChange('list')}
                sx={{
                  bgcolor: viewMode === 'list' ? 'background.paper' : 'transparent',
                  color: viewMode === 'list' ? 'primary.main' : 'text.secondary',
                  boxShadow: viewMode === 'list' ? 1 : 0,
                  borderRadius: 1,
                }}
              >
                <List size={16} />
              </IconButton>
            </Box>
          )}
          {canManageCategories && (
            <Tooltip title="Activity">
              <IconButton onClick={onOpenActivity}>
                <History size={18} />
              </IconButton>
            </Tooltip>
          )}
          {canManageCategories && selectedFolder && (
            <Tooltip title="Share folder">
              <IconButton onClick={onShareFolder}>
                <Share2 size={18} />
              </IconButton>
            </Tooltip>
          )}
          {canManageCategories && selectedFolder && (
            <Tooltip title="Rename folder">
              <IconButton onClick={onRenameFolder}>
                <Pencil size={18} />
              </IconButton>
            </Tooltip>
          )}
          {canManageCategories && selectedFolder && (
            selectedFolder.isActive ? (
              canDeleteFolder ? (
                <Tooltip title="Delete empty folder">
                  <IconButton color="error" onClick={onDeleteFolder}>
                    <Trash2 size={18} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Hide folder (has files)">
                  <IconButton color="error" onClick={onHideFolder}>
                    <Trash2 size={18} />
                  </IconButton>
                </Tooltip>
              )
            ) : (
              <Tooltip title="Unhide folder">
                <IconButton color="primary" disabled={restoringFolder} onClick={onUnhideFolder}>
                  <Eye size={18} />
                </IconButton>
              </Tooltip>
            )
          )}
          {canManageCategories && (
            <Tooltip title={selectedFolder ? 'New subfolder' : 'New folder'}>
              <IconButton onClick={onNewFolder}>
                <FolderPlus size={18} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={
              !canUpload
                ? 'Select a folder shared with you to upload or add a Drive link'
                : 'Upload or add a file'
            }
          >
            <span>
              <Button
                variant="contained"
                startIcon={<Upload size={16} />}
                disabled={!canUpload}
                onClick={onUpload}
              >
                Upload
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export default DocumentsToolbar;
